export const SMS_CONSENT_TEXT = {
    disclosure: 'Texts are optional and never required to book. Msg & data rates may apply. Reply HELP for help, STOP to opt out.',
};

const SMS_COMPLIANCE_KEYWORDS = {
    stop: 'opt_out',
    stopall: 'opt_out',
    unsubscribe: 'opt_out',
    cancel: 'opt_out',
    end: 'opt_out',
    quit: 'opt_out',
    start: 'opt_in',
    unstop: 'opt_in',
    help: 'help',
};

export function parseSmsComplianceKeyword(body, { optedOut = false } = {}) {
    if (typeof body !== 'string') return null;
    const normalized = body.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalized === 'yes') return optedOut ? 'opt_in' : null;
    return SMS_COMPLIANCE_KEYWORDS[normalized] ?? null;
}

export const DEFAULT_COMMUNICATION_PREFERENCES = {
    trip_updates: {
        enabled: true,
        email: true,
        text: false,
        alerts: {
            reservation_confirmations: false,
            upcoming_trip_details_24hrs: false,
            driver_vehicle_location: false,
            thank_you: false,
            post_trip_reminder: true,
        },
    },
    offers_news: {
        enabled: false,
        by_text: false,
        by_email: false,
    },
};

export function normalizeCommunicationPreferences(value, legacy = {}) {
    const supplied = value && typeof value === 'object' ? value : {};
    const tripUpdates = supplied.trip_updates || {};
    const offersNews = supplied.offers_news || {};
    const text = tripUpdates.text ?? legacy.sms_transactional_consent ?? false;
    const optedOut = Boolean(legacy.sms_opted_out_at);
    const offersByText = offersNews.by_text ?? legacy.sms_marketing_consent ?? false;
    return {
        trip_updates: {
            enabled: tripUpdates.enabled !== false,
            email: tripUpdates.email !== false,
            text: optedOut ? false : Boolean(text),
            alerts: {
                reservation_confirmations: Boolean(tripUpdates.alerts?.reservation_confirmations),
                upcoming_trip_details_24hrs: Boolean(tripUpdates.alerts?.upcoming_trip_details_24hrs),
                driver_vehicle_location: Boolean(tripUpdates.alerts?.driver_vehicle_location),
                thank_you: Boolean(tripUpdates.alerts?.thank_you),
                post_trip_reminder: tripUpdates.alerts?.post_trip_reminder !== false,
            },
        },
        offers_news: {
            enabled: Boolean(offersNews.enabled ?? offersByText),
            by_text: optedOut ? false : Boolean(offersByText),
            by_email: Boolean(offersNews.by_email),
        },
    };
}

export function buildCommunicationPreferencesText(preferences = DEFAULT_COMMUNICATION_PREFERENCES) {
    const current = normalizeCommunicationPreferences(preferences);
    return [
        `Trip updates: ${current.trip_updates.enabled ? 'on' : 'off'}`,
        `Email: ${current.trip_updates.email ? 'on' : 'off'}`,
        `Text messages: ${current.trip_updates.text ? 'on' : 'off'}`,
        `Reservation Confirmations: ${current.trip_updates.alerts.reservation_confirmations ? 'on' : 'off'}`,
        `Upcoming Trip Details (24hrs): ${current.trip_updates.alerts.upcoming_trip_details_24hrs ? 'on' : 'off'}`,
        `Driver/Vehicle Location: ${current.trip_updates.alerts.driver_vehicle_location ? 'on' : 'off'}`,
        `Thank You!: ${current.trip_updates.alerts.thank_you ? 'on' : 'off'}`,
        `Rating reminder: ${current.trip_updates.alerts.post_trip_reminder ? 'on' : 'off'}`,
        `Offers & news: ${current.offers_news.enabled ? 'on' : 'off'}`,
        `Offers by text: ${current.offers_news.by_text ? 'on' : 'off'}`,
        `Offers by email: ${current.offers_news.by_email ? 'on' : 'off'}`,
        SMS_CONSENT_TEXT.disclosure,
    ].join('\n');
}

/** @param {{transactional?: boolean, marketing?: boolean, preferences?: any, previousPreferences?: any, source?: string, userId?: string|null, customerAgreedVerbally?: boolean, customerAgreementNote?: string|null, consentText?: string|null, optedOutAt?: string|null, now?: string}} options */
export function buildSmsConsentPatch({
    transactional,
    marketing,
    preferences,
    previousPreferences = DEFAULT_COMMUNICATION_PREFERENCES,
    source = 'staff',
    userId = null,
    customerAgreedVerbally = false,
    customerAgreementNote = null,
    consentText = null,
    optedOutAt = null,
    now = new Date().toISOString(),
} = {}) {
    if (!['website', 'staff', 'customer_portal', 'inbound_text', 'keyword'].includes(source)) {
        throw new Error('Choose a valid SMS consent source.');
    }
    const requestedPreferences = normalizeCommunicationPreferences(preferences, {
        sms_transactional_consent: transactional,
        sms_marketing_consent: marketing,
    });
    const previous = normalizeCommunicationPreferences(previousPreferences);
    const enablingText = requestedPreferences.trip_updates.text && !previous.trip_updates.text;
    const enablingOfferText = requestedPreferences.offers_news.by_text && !previous.offers_news.by_text;
    const enablingOffers = requestedPreferences.offers_news.enabled && !previous.offers_news.enabled;
    if (source === 'staff' && optedOutAt && (enablingText || enablingOfferText)) {
        throw new Error('Only the customer can turn text messages back on after STOP. Ask them to text START or UNSTOP.');
    }
    if (source === 'staff' && (enablingText || enablingOfferText || enablingOffers) && !customerAgreedVerbally) {
        throw new Error('Record that the customer agreed to texts before enabling text messages or offers.');
    }
    const resolvedPreferences = normalizeCommunicationPreferences(requestedPreferences, { sms_opted_out_at: optedOutAt });
    return {
        communication_preferences: resolvedPreferences,
        sms_transactional_consent: Boolean(resolvedPreferences.trip_updates.text),
        sms_marketing_consent: Boolean(resolvedPreferences.offers_news.enabled && resolvedPreferences.offers_news.by_text),
        sms_consent_source: source,
        sms_consent_at: now,
        sms_consent_text: typeof consentText === 'string' && consentText
            ? consentText
            : buildCommunicationPreferencesText(resolvedPreferences),
        sms_consent_by: source === 'staff' ? userId : null,
        sms_customer_agreed_verbally: source === 'staff' && Boolean(customerAgreedVerbally),
        sms_consent_note: source === 'staff' && customerAgreedVerbally
            ? (customerAgreementNote?.trim() || null)
            : null,
        ...(optedOutAt ? { sms_opted_out_at: optedOutAt } : {}),
        ...((source === 'keyword'
            && !optedOutAt
            && (resolvedPreferences.trip_updates.text || resolvedPreferences.offers_news.by_text))
            ? { sms_opted_out_at: null }
            : {}),
    };
}

// Friendly name for where a consent change came from. Values without a friendly name show as stored.
export function consentSourceLabel(source) {
    return source === 'client_app_setup' ? 'Client app setup' : source;
}

export function smsOptOutMessage(optedOutAt) {
    if (!optedOutAt) return 'Texts are off for this customer.';
    const date = new Date(optedOutAt);
    if (!Number.isFinite(date.getTime())) return 'Texts are off for this customer.';
    return `Texts are off for this customer. They replied STOP on ${date.toLocaleDateString()}.`;
}

export function buildSmsKeywordPatch(action, {
    body = '',
    now = new Date().toISOString(),
    preferences = DEFAULT_COMMUNICATION_PREFERENCES,
} = {}) {
    if (action === 'opt_out') {
        const current = normalizeCommunicationPreferences(preferences);
        current.trip_updates.text = false;
        current.trip_updates.alerts = Object.fromEntries(
            Object.keys(current.trip_updates.alerts).map((key) => [
                key, key === 'post_trip_reminder' ? current.trip_updates.alerts[key] : false,
            ])
        );
        current.offers_news.by_text = false;
        current.offers_news.enabled = current.offers_news.by_email;
        return {
            ...buildSmsConsentPatch({
                preferences: current,
                source: 'keyword',
                now,
            }),
            sms_opted_out_at: now,
            sms_consent_text: body,
        };
    }
    if (action === 'opt_in') {
        const current = normalizeCommunicationPreferences(preferences);
        current.trip_updates.text = true;
        return {
            ...buildSmsConsentPatch({
                preferences: current,
                source: 'keyword',
                now,
            }),
            sms_opted_out_at: null,
            sms_consent_text: body,
        };
    }
    return null;
}
